/* global globalThis */
/**
 * Propose tools — open the existing gateway create/edit modal pre-populated
 * with the AI's proposal. The user reviews, fills in any secrets, and submits.
 *
 * Each tool dispatches a CustomEvent on `window`. GatewayApp listens, navigates
 * to the right section if needed, then re-fires the event scoped to the page.
 * The page's create-modal opens in "create-with-prefill" mode.
 *
 * NEVER include secrets (passwords, tokens, API keys) in the proposal —
 * the form has its own secret-input field for the user to fill in.
 */

import * as api from "../../api";
import {
  controllerLifecycleActionAvailability,
  validPLCControllerLifecycleProposal,
} from "../../pages/gateway/sections/plc-controllers-model";
import {
  classifyEdgeTag,
  edgeTagRequirement,
  normalizeEdgeTagCatalog,
} from "../../pages/gateway/sections/plc-edge-tag-catalog-model";

const dispatch = (eventName, detail) => {
  if (typeof window === "undefined") {
    return { ok: false, error: { code: "NO_WINDOW", message: "Cannot dispatch UI event in non-browser context." } };
  }
  try {
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
    return { ok: true, data: { event: eventName, opened: true } };
  } catch (err) {
    return {
      ok: false,
      error: { code: "DISPATCH_FAILED", message: err?.message || "Failed to dispatch event" },
    };
  }
};

const secureMutationKey = () => {
  const cryptoAPI = typeof window !== "undefined" ? window.crypto : globalThis.crypto;
  if (typeof cryptoAPI?.randomUUID === "function") return cryptoAPI.randomUUID();
  if (typeof cryptoAPI?.getRandomValues !== "function") {
    throw new Error("Secure browser randomness is unavailable.");
  }
  const bytes = cryptoAPI.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const exactPublicController = (controllers, name, targetName) => {
  const matches = controllers.filter((controller) =>
    controller?.name === name && (!targetName || controller?.target?.name === targetName)
  );
  return matches.length === 1 ? matches[0] : null;
};

const canonicalReviewToken = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(String(value || ""));
const canonicalSHA256 = (value) => /^[0-9a-f]{64}$/.test(String(value || ""));
const OPCUA_AI_PUBLIC_FIELDS = new Set([
  "listen_address", "namespace_uri", "source_cidrs", "identity_mode", "identity_provider",
  "max_channels", "max_sessions", "max_subscriptions_per_session",
  "max_monitored_items_per_subscription", "max_nodes_per_read", "max_nodes_per_write",
]);

export const propose_datasource = {
  name: "propose_datasource",
  description:
    "Open the New Connection form pre-filled with a proposed database connection. " +
    "User reviews, types the password, and clicks Connect. Do NOT include the password.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      type: { type: "string", enum: ["postgresql", "mysql", "influxdb", "prometheus"] },
      host: { type: "string" },
      port: { type: "integer" },
      database: { type: "string" },
      username: { type: "string" },
      ssl_mode: { type: "string", enum: ["disable", "require"] },
      org: { type: "string" },
    },
    required: ["name", "type"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-datasource", { payload: args }),
};

export const propose_api_profile = {
  name: "propose_api_profile",
  description:
    "Open the New API Profile form pre-filled with a proposed HTTP polling profile. " +
    "Do NOT include auth_secret — the user fills that in.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      url: { type: "string", minLength: 1 },
      method: { type: "string", enum: ["GET", "POST", "PUT", "PATCH", "DELETE"] },
      poll_interval_ms: { type: "integer" },
      timeout_ms: { type: "integer" },
      auth_mode: { type: "string", enum: ["none", "bearer", "basic"] },
      auth_username: { type: "string" },
      headers: { type: "object" },
    },
    required: ["name", "url"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-api-profile", { payload: args }),
};

export const propose_credential = {
  name: "propose_credential",
  description:
    "Open the New Credential form pre-filled. Do NOT include secret values.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      auth_mode: { type: "string", enum: ["none", "bearer", "basic", "oauth2"] },
      provider: { type: "string" },
      username: { type: "string" },
      description: { type: "string" },
    },
    required: ["name", "auth_mode"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-credential", { payload: args }),
};

export const propose_oauth_app = {
  name: "propose_oauth_app",
  description:
    "Open the OAuth app form for a provider. Do NOT include client_secret — the user fills it in.",
  parameters: {
    type: "object",
    properties: {
      provider: { type: "string", minLength: 1 },
      client_id: { type: "string" },
      redirect_url: { type: "string" },
      scopes: { type: "array", items: { type: "string" } },
    },
    required: ["provider"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-oauth-app", { payload: args }),
};

export const propose_mqtt_broker = {
  name: "propose_mqtt_broker",
  description:
    "Open the New MQTT Broker form pre-filled. Do NOT include the password.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      host: { type: "string", minLength: 1 },
      port: { type: "integer" },
      tls: { type: "boolean" },
      client_id: { type: "string" },
      username: { type: "string" },
      keepalive_seconds: { type: "integer" },
    },
    required: ["name", "host"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-mqtt-broker", { payload: args }),
};

export const propose_historian_provider = {
  name: "propose_historian_provider",
  description:
    "Open the New Historian Provider form pre-filled with a proposed external historian " +
    "(TimescaleDB / Tiger Cloud) — a backend where a tag's HISTORY is STORED. Use this " +
    "(NOT propose_datasource) whenever the user wants to connect a historian, store tag " +
    "history externally, or 'connect this historian' with Tiger Cloud/Timescale details. " +
    "Tiger Cloud is Postgres-compatible, so only the INTENT distinguishes it from a " +
    "database connection. Do NOT include the password — the user types it in.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      host: { type: "string", minLength: 1 },
      port: { type: "integer" },
      database: { type: "string" },
      user: { type: "string" },
      sslmode: {
        type: "string",
        enum: ["disable", "allow", "prefer", "require", "verify-ca", "verify-full"],
      },
      retention_days: { type: "integer" },
    },
    required: ["name", "host"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-historian-provider", { payload: args }),
};

export const propose_user = {
  name: "propose_user",
  description:
    "Open the New User form pre-filled. The form will collect a temporary password / invite from the admin.",
  parameters: {
    type: "object",
    properties: {
      username: { type: "string", minLength: 1 },
      email: { type: "string" },
      role: { type: "string", enum: ["admin", "user", "viewer"] },
      first_name: { type: "string" },
      last_name: { type: "string" },
    },
    required: ["username"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-user", { payload: args }),
};

export const propose_group = {
  name: "propose_group",
  description: "Open the New Group form pre-filled.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      description: { type: "string" },
    },
    required: ["name"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-group", { payload: args }),
};

export const propose_project = {
  name: "propose_project",
  description: "Open the New Project form pre-filled.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      description: { type: "string" },
    },
    required: ["name"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-project", { payload: args }),
};

export const propose_ai_provider = {
  name: "propose_ai_provider",
  description:
    "Open the New AI Provider form pre-filled. Use for adding an LLM backend " +
    "(OpenAI, Anthropic, xAI, Google Gemini, Ollama, or a custom endpoint). " +
    "Do NOT include the API key — the user pastes it in the form. The form " +
    "auto-fills the endpoint from `provider_type`; pass a custom `endpoint` " +
    "only when provider_type is `custom`.",
  parameters: {
    type: "object",
    properties: {
      name: { type: "string", minLength: 1 },
      provider_type: {
        type: "string",
        enum: ["openai", "anthropic", "xai", "google", "ollama", "custom"],
      },
      endpoint: { type: "string" },
      default_model: { type: "string" },
      is_default: { type: "boolean" },
    },
    required: ["name", "provider_type"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-ai-provider", { payload: args }),
};

export const propose_alarm = {
  name: "propose_alarm",
  description:
    "Open the New Alarm Rule form (on `alarms-notifications`) pre-filled with a proposed alarm rule. The user reviews and submits. ALWAYS resolve `tag_id` first via `list_tags` — never invent a tag id. The mode determines which setpoints are needed: `above`/`below` use a single `setpoint`; `between`/`outside_range` need both `setpoint` AND `setpoint_high`. **`message` is REQUIRED** — the form's Create button is disabled until message, tag_id, and setpoint are all set, so without `message` the user opens a dead form. Severities map to UI badges.",
  parameters: {
    type: "object",
    properties: {
      tag_id: { type: "string", minLength: 1 },
      name: { type: "string", minLength: 1 },
      mode: { type: "string", enum: ["above", "below", "between", "outside_range"] },
      setpoint: { type: "number" },
      setpoint_high: { type: "number" },
      severity: { type: "string", enum: ["critical", "high", "medium", "low"] },
      message: { type: "string", minLength: 1 },
      on_delay_ms: { type: "integer" },
      off_delay_ms: { type: "integer" },
      notify_all: { type: "boolean" },
      notify_user_ids: { type: "array", items: { type: "string" } },
      notify_group_ids: { type: "array", items: { type: "string" } },
    },
    required: ["tag_id", "name", "mode", "setpoint", "message"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-alarm", { payload: args }),
};

export const propose_edge_gpio_binding = {
  name: "propose_edge_gpio_binding",
	description:
	  "Open the selected runtime's unified I/O & connections wizard with GPIO preselected. " +
	  "Call list_edge_runtimes and get_edge_connectors first; use only a point returned in the detected board's available_pins, then use the real gateway_id. bcm_pin is the signed catalog point identity even when the UI presents an industrial DIO terminal. " +
	  "Unknown or mismatched boards cannot use generic pin fallback. The arguments are review context only; this tool never creates a legacy GPIO binding or bypasses signed connection review.",
  parameters: {
    type: "object",
    properties: {
      gateway_id: { type: "string", minLength: 1 },
      name: { type: "string", minLength: 1 },
      bcm_pin: { type: "integer", minimum: 0, maximum: 27 },
      direction: { type: "string", enum: ["input", "output"] },
      bias: { type: "string", enum: ["disabled", "pull_up", "pull_down"] },
      debounce_ms: { type: "integer", enum: [0, 20, 50, 100] },
      active_low: { type: "boolean" },
      safe_state: { type: "boolean" },
    },
    required: ["gateway_id", "name", "bcm_pin", "direction"],
    additionalProperties: false,
  },
  execute: async (args) => {
    try {
      const [gateways, inventory] = await Promise.all([
        api.listEdgeGateways(),
        api.listEdgeConnectors(args.gateway_id),
      ]);
      const gateway = (Array.isArray(gateways) ? gateways : []).find((candidate) => candidate.id === args.gateway_id);
      if (!gateway) return { ok: false, error: { code: "EDGE_RUNTIME_NOT_FOUND", message: "The selected runtime is not enrolled." } };
      if (inventory?.configuration_available !== true) {
        return { ok: false, error: { code: "EDGE_CONNECTION_SETUP_UNAVAILABLE", message: "I/O setup is unavailable for this workspace. Ask a workspace administrator to finish connection setup, then retry." } };
      }
      if (inventory?.runtime?.connection !== "online" || inventory?.runtime?.snapshot_stale !== false) {
        return { ok: false, error: { code: "EDGE_RUNTIME_SNAPSHOT_NOT_FRESH", message: "The selected runtime must be online with a current device report before a digital I/O draft can be opened." } };
      }
      const board = inventory?.gpio_board;
      const pins = Array.isArray(board?.available_pins) ? board.available_pins : [];
      const pin = pins.find((candidate) => Number(candidate?.bcm) === args.bcm_pin && candidate?.policy !== "reserved");
      const capabilities = new Set(Array.isArray(inventory?.capabilities) ? inventory.capabilities : []);
      const boardCapability = typeof board?.profile === "string" && board.profile
        ? `gpio-board-profile:${board.profile}`
        : "";
      if (board?.status !== "detected" || !capabilities.has("connector-config-v1") || !capabilities.has("gpio") || !boardCapability || !capabilities.has(boardCapability)) {
        return { ok: false, error: { code: "EDGE_GPIO_UNSUPPORTED", message: "The selected runtime does not report complete built-in digital I/O support." } };
      }
      if (!pin) {
        return { ok: false, error: { code: "EDGE_GPIO_POINT_NOT_AVAILABLE", message: "That digital point is not available in the runtime's current hardware report." } };
      }
      const opened = dispatch("gw-ai-propose-edge-gpio", { payload: args });
      if (!opened.ok) return opened;
      return {
        ok: true,
        data: {
          runtime_name: gateway.name,
          point: pin.label || `GPIO${args.bcm_pin}`,
          opened_for_human_review: true,
          connection_preview_created: false,
          queued: false,
          confirmation_required: true,
        },
      };
    } catch (error) {
      return { ok: false, error: { code: "EDGE_GPIO_PROPOSAL_UNAVAILABLE", message: error?.message || "The selected runtime could not be inspected." } };
    }
  },
};

export const propose_edge_modbus_rtu_connection = {
  name: "propose_edge_modbus_rtu_connection",
  description:
    "Open an enrolled runtime's I/O & connections wizard with a Modbus RTU device and its first point prefilled for human review. The operator can add the remaining points in the same form. " +
    "Call list_edge_runtimes, get_edge_connectors, and inspect_edge_runtime_tags first. Use the exact detected serial_port and exact zero-based point address from the field-device manual; never guess serial format, unit ID, function, codec, scale, or wiring. " +
    "For an FR201/FR202, detecting the tty proves only that the OnLogic serial bridge exists—it cannot prove whether its EEPROM is set to RS-232, RS-422, or RS-485, so the operator must confirm the electrical mode and terminal wiring separately. " +
    "For an output, choose readback only when the device manual and bench test prove that the written point can be read. Use device_response for a write-only device; that proves only that the Modbus write was accepted, not that the relay or field output physically changed. " +
    "When the device manual requires reads to use an exact block rather than the smallest point range, provide both read_window_start and read_window_count; never infer them from another model. " +
    "The tool only opens a draft. It does not create a tag, preview or confirm the connection, send a device command, or prove I/O. The user reviews the tag and device settings, then uses the normal two-step connection confirmation.",
  parameters: {
    type: "object",
    properties: {
      gateway_id: { type: "string", minLength: 1 },
      connection_name: { type: "string", minLength: 1, maxLength: 160 },
      direction: { type: "string", enum: ["input", "output"] },
      tag_path: { type: "string", minLength: 1, maxLength: 2048 },
      new_tag_name: { type: "string", minLength: 1, maxLength: 64 },
      tag_type: { type: "string", enum: ["boolean", "integer", "number"] },
      serial_port: { type: "string", minLength: 1, maxLength: 4096 },
      baud_rate: { type: "integer", minimum: 300, maximum: 4000000 },
      parity: { type: "string", enum: ["none", "even", "odd"] },
      data_bits: { type: "integer", enum: [8] },
      stop_bits: { type: "integer", enum: [1, 2] },
      unit_id: { type: "integer", minimum: 1, maximum: 247 },
      function_code: { type: "integer", minimum: 1, maximum: 4 },
      zero_based_address: { type: "integer", minimum: 0, maximum: 65535 },
      value_type: { type: "string", enum: ["bool", "int16", "uint16", "int32", "uint32", "int64", "uint64", "float32", "float64"] },
      register_bit: { type: "integer", minimum: 0, maximum: 15 },
      byte_order: { type: "string", enum: ["big", "little"] },
      word_order: { type: "string", enum: ["big", "little"] },
      scale: { type: "number" },
      offset: { type: "number" },
      poll_interval_ms: { type: "integer", minimum: 50, maximum: 3600000 },
      timeout_ms: { type: "integer", minimum: 50, maximum: 60000 },
      output_confirmation: { type: "string", enum: ["readback", "device_response"] },
      read_window_start: { type: "integer", minimum: 0, maximum: 65535 },
      read_window_count: { type: "integer", minimum: 1, maximum: 2000 },
    },
    required: [
      "gateway_id", "connection_name", "direction", "tag_type", "serial_port",
      "baud_rate", "parity", "data_bits", "stop_bits", "unit_id",
      "function_code", "zero_based_address", "value_type", "byte_order",
      "word_order", "scale", "offset", "poll_interval_ms", "timeout_ms",
    ],
    additionalProperties: false,
  },
  execute: async (args) => {
    const existingPath = String(args.tag_path || "").trim();
    const newTagName = String(args.new_tag_name || "").trim();
    if ((existingPath ? 1 : 0) + (newTagName ? 1 : 0) !== 1) {
      return { ok: false, error: { code: "EDGE_MODBUS_TAG_REQUIRED", message: "Provide exactly one existing tag_path or one new_tag_name." } };
    }
    const booleanPoint = args.tag_type === "boolean";
    if ((booleanPoint && ![1, 2, 3, 4].includes(args.function_code)) || (!booleanPoint && ![3, 4].includes(args.function_code))) {
      return { ok: false, error: { code: "EDGE_MODBUS_POINT_TYPE_MISMATCH", message: "The function code does not match the selected tag type." } };
    }
    if (args.direction === "output") {
      if (!["readback", "device_response"].includes(args.output_confirmation)) {
        return { ok: false, error: { code: "EDGE_MODBUS_OUTPUT_CONFIRMATION_REQUIRED", message: "Choose readback or device response using the field-device manual and bench behavior." } };
      }
      const writableRegister = args.function_code === 3 && ["int16", "uint16"].includes(args.value_type);
      const writableCoil = args.function_code === 1 && args.value_type === "bool";
      const writableRegisterBit = args.function_code === 3 && args.value_type === "bool" && Number.isInteger(args.register_bit);
      if (!writableRegister && !writableCoil && !writableRegisterBit) {
        return { ok: false, error: { code: "EDGE_MODBUS_WRITE_UNSUPPORTED", message: "Writable RTU points must fit one coil or one 16-bit holding register." } };
      }
    }
    const integerTypes = new Set(["int16", "uint16", "int32", "uint32", "int64", "uint64"]);
    const floatTypes = new Set(["float32", "float64"]);
    if ((booleanPoint && args.value_type !== "bool") ||
        (args.tag_type === "integer" && !integerTypes.has(args.value_type)) ||
        (args.tag_type === "number" && !integerTypes.has(args.value_type) && !floatTypes.has(args.value_type))) {
      return { ok: false, error: { code: "EDGE_MODBUS_VALUE_TYPE_MISMATCH", message: "The Modbus value format does not produce the selected tag type." } };
    }
    if (booleanPoint && [3, 4].includes(args.function_code) && !Number.isInteger(args.register_bit)) {
      return { ok: false, error: { code: "EDGE_MODBUS_REGISTER_BIT_REQUIRED", message: "A Boolean register point requires register_bit 0 through 15." } };
    }
    if (args.tag_type === "integer" && (args.scale !== 1 || args.offset !== 0)) {
      return { ok: false, error: { code: "EDGE_MODBUS_INTEGER_TRANSFORM_INVALID", message: "Use a Number tag when scale or offset changes the raw integer." } };
    }
    if (args.tag_type === "number" && integerTypes.has(args.value_type) && args.scale === 1 && args.offset === 0) {
      return { ok: false, error: { code: "EDGE_MODBUS_NUMBER_TRANSFORM_REQUIRED", message: "An integer register feeding a Number tag needs a non-identity scale or offset." } };
    }
    if (!Number.isFinite(args.scale) || args.scale === 0 || !Number.isFinite(args.offset)) {
      return { ok: false, error: { code: "EDGE_MODBUS_TRANSFORM_INVALID", message: "Scale must be finite and non-zero, and offset must be finite." } };
    }
    const hasReadWindowStart = Number.isInteger(args.read_window_start);
    const hasReadWindowCount = Number.isInteger(args.read_window_count);
    if (hasReadWindowStart !== hasReadWindowCount) {
      return { ok: false, error: { code: "EDGE_MODBUS_READ_WINDOW_INCOMPLETE", message: "Provide both the fixed read block start and size." } };
    }
    if (hasReadWindowStart) {
      const width = args.function_code <= 2 || args.value_type === "bool"
        ? 1
        : (["int32", "uint32", "float32"].includes(args.value_type) ? 2 : ["int64", "uint64", "float64"].includes(args.value_type) ? 4 : 1);
      const limit = args.function_code <= 2 ? 2000 : 125;
      if (args.read_window_count < 1 || args.read_window_count > limit ||
          args.read_window_start + args.read_window_count > 65536 ||
          args.zero_based_address < args.read_window_start ||
          args.zero_based_address + width > args.read_window_start + args.read_window_count) {
        return { ok: false, error: { code: "EDGE_MODBUS_READ_WINDOW_INVALID", message: "The point must fit inside a protocol-sized fixed read block." } };
      }
    }
    try {
      const [gateways, inventory] = await Promise.all([
        api.listEdgeGateways(),
        api.listEdgeConnectors(args.gateway_id),
      ]);
      const gateway = (Array.isArray(gateways) ? gateways : []).find((candidate) => candidate.id === args.gateway_id);
      if (!gateway) return { ok: false, error: { code: "EDGE_RUNTIME_NOT_FOUND", message: "The selected runtime is not enrolled." } };
      if (inventory?.configuration_available !== true) {
        return { ok: false, error: { code: "EDGE_CONNECTION_SETUP_UNAVAILABLE", message: "I/O setup is unavailable for this workspace. Ask a workspace administrator to finish connection setup, then retry." } };
      }
      if (inventory?.runtime?.connection !== "online" || inventory?.runtime?.snapshot_stale !== false) {
        return { ok: false, error: { code: "EDGE_RUNTIME_SNAPSHOT_NOT_FRESH", message: "The selected runtime must be online with fresh device evidence before an RTU draft can be opened." } };
      }
      const capabilities = new Set(Array.isArray(inventory?.capabilities) ? inventory.capabilities : []);
      if (!["connector-config-v1", "edge-tag-catalog-v1", "edge-tag-identity-v1", "modbus-rtu-client"].every((item) => capabilities.has(item))) {
        return { ok: false, error: { code: "EDGE_MODBUS_RTU_UNSUPPORTED", message: "The selected runtime does not report complete Modbus RTU support." } };
      }
      const serialPorts = Array.isArray(inventory?.serial_ports) ? inventory.serial_ports.map(String) : [];
      if (!serialPorts.includes(args.serial_port)) {
        return { ok: false, error: { code: "EDGE_MODBUS_SERIAL_PORT_NOT_REPORTED", message: "The selected serial port is not in the runtime's fresh device inventory." } };
      }
      if (existingPath) {
        const catalog = normalizeEdgeTagCatalog(await api.inspectEdgeTagCatalog(args.gateway_id, [existingPath]));
        const dataType = args.tag_type === "boolean" ? "bool" : args.tag_type === "integer" ? "int64" : "float64";
        const eligibility = classifyEdgeTag(catalog, existingPath, { dataType, writable: args.direction === "output" });
        if (!eligibility.eligible) {
          return { ok: false, error: { code: "EDGE_MODBUS_TAG_NOT_ELIGIBLE", message: `${existingPath}: ${eligibility.message}` } };
        }
      }
      const payload = {
        gateway_id: args.gateway_id,
        name: args.connection_name,
        direction: args.direction,
        tag_mode: existingPath ? "existing" : "new",
        ...(existingPath ? { tag_path: existingPath } : { new_tag_name: newTagName, new_tag_type: args.tag_type }),
        settings: {
          serialPort: args.serial_port,
          baudRate: String(args.baud_rate),
          parity: args.parity,
          dataBits: String(args.data_bits),
          stopBits: String(args.stop_bits),
          unitId: String(args.unit_id),
          function: String(args.function_code),
          address: String(args.zero_based_address),
          valueType: args.value_type,
          bit: String(args.register_bit ?? 0),
          byteOrder: args.byte_order,
          wordOrder: args.word_order,
          scale: String(args.scale),
          offset: String(args.offset),
          pollIntervalMs: String(args.poll_interval_ms),
          timeoutMs: String(args.timeout_ms),
          writeConfirmation: args.direction === "output" && args.output_confirmation === "device_response" ? "echo" : "readback",
          ...(hasReadWindowStart ? {
            readWindowMode: "fixed",
            readWindowStart: String(args.read_window_start),
            readWindowCount: String(args.read_window_count),
          } : {}),
        },
      };
      const opened = dispatch("gw-ai-propose-edge-modbus-rtu", { payload });
      if (!opened.ok) return opened;
      return {
        ok: true,
        data: {
          runtime_name: gateway.name,
          connection_name: args.connection_name,
          direction: args.direction,
          opened_for_human_review: true,
          tag_created: false,
          connection_preview_created: false,
          queued: false,
          confirmation_required: true,
          commissioning_gate: args.direction === "output" && args.output_confirmation === "device_response"
            ? "The device response confirms only that the write was accepted. Verify the physical output independently before field use."
            : "Confirm the electrical mode, wiring, scaling, and real device behavior before field use.",
        },
      };
    } catch (error) {
      return { ok: false, error: { code: "EDGE_MODBUS_RTU_PROPOSAL_UNAVAILABLE", message: error?.message || "The selected runtime could not be inspected." } };
    }
  },
};

export const propose_edge_modbus_tcp_server = {
  name: "propose_edge_modbus_tcp_server",
  description:
    "Open Servers with a non-executing Modbus TCP server draft for one enrolled runtime. " +
		"Call list_edge_runtimes, get_edge_connectors, and inspect_edge_runtime_tags first, use a real gateway_id, and proceed only when the selected runtime is online with fresh connector and tag-catalog evidence advertising connector-config-v1, modbus-tcp-server, edge-tag-catalog-v1, and edge-tag-identity-v1. " +
    "Use one concrete device IP, an unprivileged port, and specific source CIDRs; wildcard, hostname, multicast, and catch-all exposure is rejected. Running requires at least one mapping, while Stopped may intentionally use an empty map. Every mapped path must already exist in that runtime's local tag store through a deployed PLC program, GPIO point, or client connector; cloud catalog presence does not prove edge availability and no automatic tag mirroring occurs. Modbus TCP has no application authentication, encryption, or integrity, so a Running review requires an unchecked human acknowledgement of source allowlisting and network segmentation that AI cannot set. The tool never creates a signed revision, confirms a digest, queues a command, or opens a listener. The human must review the draft, request the exact server preview, acknowledge the security boundary when required, and separately confirm it.",
  parameters: {
    type: "object",
    properties: {
      gateway_id: { type: "string", minLength: 1 },
      reason: { type: "string", minLength: 3, maxLength: 500 },
      desired_state: { type: "string", enum: ["running", "stopped"] },
      listen_host: { type: "string", minLength: 2, maxLength: 128 },
      listen_port: { type: "integer", minimum: 1024, maximum: 65535 },
      unit_id: { type: "integer", minimum: 0, maximum: 255 },
      max_clients: { type: "integer", minimum: 1, maximum: 1024 },
      request_timeout_ms: { type: "integer", minimum: 100, maximum: 60000 },
      idle_timeout_ms: { type: "integer", minimum: 1000, maximum: 3600000 },
      source_cidrs: { type: "array", maxItems: 64, uniqueItems: true, items: { type: "string", minLength: 3, maxLength: 128 } },
      mappings: {
        type: "array",
        maxItems: 100,
        items: {
          type: "object",
          properties: {
            table: { type: "string", enum: ["coils", "discrete_inputs", "holding_registers", "input_registers"] },
            address: { type: "integer", minimum: 0, maximum: 65535 },
            tag_path: { type: "string", minLength: 1, maxLength: 2048 },
            value_type: { type: "string", enum: ["bool", "uint16", "int16", "uint32", "int32", "uint64", "int64", "float32", "float64"] },
            byte_order: { type: "string", enum: ["big", "little"] },
            word_order: { type: "string", enum: ["big", "little"] },
          },
          required: ["table", "address", "tag_path", "value_type"],
          additionalProperties: false,
        },
      },
    },
    required: ["gateway_id", "reason", "desired_state", "listen_host", "listen_port", "unit_id", "max_clients", "request_timeout_ms", "idle_timeout_ms", "source_cidrs", "mappings"],
    additionalProperties: false,
  },
  execute: async (args) => {
    if (args.desired_state === "running" && args.source_cidrs.length === 0) {
      return { ok: false, error: { code: "EDGE_MODBUS_SERVER_SOURCE_NETWORK_REQUIRED", message: "A Running edge Modbus TCP server requires at least one specific source network." } };
    }
    if (args.desired_state === "running" && args.mappings.length === 0) {
      return { ok: false, error: { code: "EDGE_MODBUS_SERVER_MAPPING_REQUIRED", message: "A Running edge Modbus TCP server requires at least one tag mapping." } };
    }
    try {
      const [gateways, inventory] = await Promise.all([
        api.listEdgeGateways(),
        api.listEdgeConnectors(args.gateway_id),
      ]);
      const gateway = (Array.isArray(gateways) ? gateways : []).find((candidate) => candidate.id === args.gateway_id);
      if (!gateway) return { ok: false, error: { code: "EDGE_RUNTIME_NOT_FOUND", message: "The selected runtime is not enrolled." } };
      if (inventory?.configuration_available !== true) {
        return { ok: false, error: { code: "EDGE_CONNECTION_SETUP_UNAVAILABLE", message: "I/O setup is unavailable for this workspace. Ask a workspace administrator to finish connection setup, then retry." } };
      }
      const capabilities = new Set(Array.isArray(inventory?.capabilities) ? inventory.capabilities : []);
      if (inventory?.runtime?.connection !== "online" || inventory?.runtime?.snapshot_stale !== false) {
        return { ok: false, error: { code: "EDGE_RUNTIME_SNAPSHOT_NOT_FRESH", message: "The selected runtime must be online with a fresh capability snapshot before a server draft can be opened." } };
      }
		if (!capabilities.has("connector-config-v1") || !capabilities.has("modbus-tcp-server") ||
			!capabilities.has("edge-tag-catalog-v1") || !capabilities.has("edge-tag-identity-v1")) {
        return { ok: false, error: { code: "EDGE_MODBUS_SERVER_UNSUPPORTED", message: "The selected runtime does not advertise the exact signed Modbus TCP server capability." } };
      }
      if (args.mappings.length > 0) {
        const paths = [...new Set(args.mappings.map((mapping) => mapping.tag_path))];
        const catalog = normalizeEdgeTagCatalog(await api.inspectEdgeTagCatalog(args.gateway_id, paths));
        const blocked = args.mappings.map((mapping) => ({
          path: mapping.tag_path,
          result: classifyEdgeTag(catalog, mapping.tag_path, edgeTagRequirement({
            table: mapping.table,
            valueType: mapping.value_type,
          })),
        })).find(({ result }) => !result.eligible);
        if (blocked) {
          return {
            ok: false,
            error: {
              code: "EDGE_MODBUS_SERVER_TAG_NOT_ELIGIBLE",
              message: `${blocked.path}: ${blocked.result.message}`,
            },
          };
        }
      }
      const opened = dispatch("gw-ai-propose-edge-modbus-server", { payload: args });
      if (!opened.ok) return opened;
      return {
        ok: true,
        data: {
          runtime_name: gateway.name,
          desired_state: args.desired_state,
          endpoint: `${args.listen_host}:${args.listen_port}`,
          mapping_count: args.mappings.length,
          source_network_count: args.source_cidrs.length,
          opened_for_human_review: true,
          signed_preview_created: false,
          queued: false,
          listening: false,
          confirmation_required: true,
        },
      };
    } catch (error) {
      return { ok: false, error: { code: "EDGE_MODBUS_SERVER_PROPOSAL_UNAVAILABLE", message: error?.message || "The selected runtime could not be inspected." } };
    }
  },
};

export const propose_plc_fleet_rollout = {
  name: "propose_plc_fleet_rollout",
  description:
    "Open PLC Fleet Rollouts and prefill a non-executing draft proposal. Call list_plc_fleet_candidates first and use only returned program_id and gateway_id values. " +
    "This tool cannot build signed packages, preview a digest, confirm, pause, cancel, or roll back a rollout. The user must review the page, build the immutable preview, then explicitly confirm it (and a different engineer may be required).",
  parameters: {
    type: "object",
    properties: {
      program_id: { type: "string", minLength: 1 },
      name: { type: "string", minLength: 1 },
      reason: { type: "string", minLength: 3 },
      run_policy: { type: "string", enum: ["preserve", "start", "stop"] },
      gateway_ids: { type: "array", minItems: 1, uniqueItems: true, items: { type: "string", minLength: 1 } },
      canary_gateway_id: { type: "string", minLength: 1 },
    },
    required: ["program_id", "name", "reason", "run_policy", "gateway_ids"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-plc-fleet-rollout", { payload: args }),
};

export const propose_plc_fleet_control = {
  name: "propose_plc_fleet_control",
  description:
    "Open an existing PLC fleet rollout with a pause, cancel, or rollback form prefilled for human review. " +
    "Call list_plc_fleet_rollouts and get_plc_fleet_rollout first, use the exact rollout_id, and explain the current target phases. " +
    "This tool does not execute the action; the operator must explicitly submit it with configure and write permissions.",
  parameters: {
    type: "object",
    properties: {
      rollout_id: { type: "string", minLength: 1 },
      action: { type: "string", enum: ["pause", "cancel", "rollback"] },
      reason: { type: "string", minLength: 3 },
      rollback_activated: { type: "boolean" },
    },
    required: ["rollout_id", "action", "reason"],
    additionalProperties: false,
  },
  execute: async (args) => dispatch("gw-ai-propose-plc-fleet-control", { payload: args }),
};

export const propose_plc_controller_draft_change = {
  name: "propose_plc_controller_draft_change",
  description:
    "Compile and preflight an exact, non-executing proposal to change the published applications and bounded resource limits of one stopped isolated virtual PLC controller draft. " +
    "Call list_plc_controllers first and use exact public controller/runtime/application names. This tool may create a ten-minute digest-bound review proposal and open it for the user, but it cannot confirm the proposal, start or stop a controller, stage or deploy software, or change plant I/O.",
  parameters: {
    type: "object",
    properties: {
      controller_name: { type: "string", minLength: 1 },
      target_name: { type: "string", minLength: 1 },
      application_names: {
        type: "array",
        uniqueItems: true,
        maxItems: 64,
        items: { type: "string", minLength: 1 },
      },
      reason: { type: "string", minLength: 3, maxLength: 500 },
      resource_limits: {
        type: "object",
        properties: {
          cpu_quota_millicores: { type: "integer", minimum: 100, maximum: 8000 },
          cpu_weight: { type: "integer", minimum: 1, maximum: 10000 },
          memory_high_bytes: { type: "integer", minimum: 100663296, maximum: 3758096384 },
          memory_max_bytes: { type: "integer", minimum: 134217728, maximum: 4294967296 },
          tasks_max: { type: "integer", minimum: 32, maximum: 256 },
          open_files_max: { type: "integer", minimum: 64, maximum: 1024 },
          state_max_bytes: { type: "integer", minimum: 16777216, maximum: 268435456 },
          state_max_files: { type: "integer", minimum: 4, maximum: 32 },
          state_max_inodes: { type: "integer", minimum: 16, maximum: 256 },
          watchdog_milliseconds: { type: "integer", minimum: 2000, maximum: 30000 },
        },
        additionalProperties: false,
      },
    },
    required: ["controller_name", "target_name", "application_names", "reason"],
    additionalProperties: false,
  },
  execute: async ({ controller_name, target_name, application_names, reason, resource_limits = {} }) => {
    try {
      const [controllerPayload, programPayload] = await Promise.all([
        api.listPLCControllers(),
        api.listPLCProgramsForFleet(),
      ]);
      const controllers = Array.isArray(controllerPayload?.controllers) ? controllerPayload.controllers : [];
      const controller = exactPublicController(controllers, controller_name, target_name);
      if (!controller) {
        return { ok: false, error: { code: "PLC_CONTROLLER_NOT_UNIQUE", message: "No unique controller matched those exact public controller and target names." } };
      }
      if (controller.managed_legacy || controller.mode !== "isolated_linux_v1" || controller.desired_state !== "stopped") {
        return { ok: false, error: { code: "PLC_CONTROLLER_NOT_EDITABLE", message: "Only a stopped isolated controller draft can be proposed for review." } };
      }
      const publishedNames = new Set(
        (Array.isArray(programPayload?.programs) ? programPayload.programs : [])
          .filter((program) => Number(program?.published_version) > 0)
          .map((program) => program.name)
      );
      const unavailable = application_names.filter((name) => !publishedNames.has(name));
      if (unavailable.length > 0) {
        return { ok: false, error: { code: "PLC_APPLICATION_NOT_PUBLISHED", message: `These exact public application names are unavailable or unpublished: ${unavailable.join(", ")}.` } };
      }
      const proposal = await api.previewPLCControllerDraftChange(controller.id, {
        expected_version: controller.version,
        program_names: application_names,
        resource_limits,
        reason,
      }, secureMutationKey());
      if (
        proposal?.schema !== "controlseat.plc.controller-draft-change-proposal" ||
        proposal?.version !== 1 || proposal?.configuration_only !== true ||
        proposal?.execution_changed !== false || proposal?.can_confirm !== true ||
        proposal?.controller?.name !== controller.name ||
        proposal?.controller?.expected_version !== controller.version ||
        proposal?.reason !== reason ||
        !canonicalReviewToken(proposal?.confirmation?.token) ||
        !canonicalSHA256(proposal?.confirmation?.proposal_sha256)
      ) {
        return { ok: false, error: { code: "PLC_CONTROLLER_PROPOSAL_INVALID", message: "The gateway did not return a valid stopped-controller review proposal." } };
      }
      const opened = dispatch("gw-ai-propose-plc-controller-draft-change", {
        payload: { controller_name: controller.name, target_name: proposal.target?.name || controller.target?.name || "", proposal },
      });
      if (!opened.ok) return opened;
      return {
        ok: true,
        data: {
          controller_name: proposal.controller?.name || controller.name,
          target_name: proposal.target?.name || controller.target?.name || "",
          configuration_only: true,
          execution_changed: false,
          confirmation_required: true,
          applications: proposal.applications || [],
          policy: proposal.policy || null,
          logical_admission: proposal.logical_admission || null,
          changes: proposal.changes || [],
          warnings: proposal.warnings || [],
          reason: proposal.reason,
          proposal_sha256: proposal.confirmation?.proposal_sha256 || "",
          expires_at: proposal.confirmation?.expires_at || "",
          opened_for_human_review: true,
        },
      };
    } catch (error) {
      return { ok: false, error: { code: "PLC_CONTROLLER_PROPOSAL_FAILED", message: error?.message || "The controller draft proposal failed." } };
    }
  },
};

export const propose_plc_controller_lifecycle = {
  name: "propose_plc_controller_lifecycle",
  description:
    "Create an exact backend-reviewed Start, Stop, Restart, Reset, or eligible Retry preview for one isolated virtual PLC controller selected by public controller and runtime name, then open its digest-bound human review dialog. " +
    "Call list_plc_controllers first. AI-proposed Restart always preserves the exact durable project RETAIN checkpoint and rejects if active forces are present. Retained-state reset and active-force clearing are manual-only choices with independent post-preview acknowledgements; a Retry inheriting either must also return to the human Gateway flow. Restart is allowed only when the backend advertises that exact action for a running application-only controller. This tool cannot confirm or queue the command and never returns controller, runtime, command, proposal-token, tag, tenant, signing, or Retry-source identities. If executable lifecycle capability or an authenticated Retry source is unavailable, report that truth instead of inventing a command.",
  parameters: {
    type: "object",
    properties: {
      controller_name: { type: "string", minLength: 1 },
      target_name: { type: "string", minLength: 1 },
      action: { type: "string", enum: ["start", "stop", "restart", "reset", "retry"] },
      reason: { type: "string", minLength: 3, maxLength: 500 },
    },
    required: ["controller_name", "target_name", "action", "reason"],
    additionalProperties: false,
  },
  execute: async ({ controller_name, target_name, action, reason }) => {
    try {
      const payload = await api.listPLCControllers();
      const controllers = Array.isArray(payload?.controllers) ? payload.controllers : [];
      const controller = exactPublicController(controllers, controller_name, target_name);
      if (!controller) {
        return { ok: false, error: { code: "PLC_CONTROLLER_NOT_UNIQUE", message: "No unique controller matched those exact public controller and runtime names." } };
      }
      const availability = controllerLifecycleActionAvailability(
        controller,
        action,
        payload?.capability,
        true
      );
      if (!availability.enabled) {
        return { ok: false, error: { code: "PLC_CONTROLLER_LIFECYCLE_UNAVAILABLE", message: availability.reason } };
      }
      const previewRequest = {
        action,
        expected_version: controller.version,
        reason,
        retained_disposition: action === "restart" ? "preserve" : "",
        force_disposition: action === "restart" ? "reject_if_present" : "",
      };
      const preview = await api.previewPLCControllerLifecycle(
        controller.id,
        previewRequest,
        secureMutationKey()
      );
      if (!validPLCControllerLifecycleProposal(preview, controller, action)) {
        return { ok: false, error: { code: "PLC_CONTROLLER_LIFECYCLE_PREVIEW_INVALID", message: "The gateway did not return a valid public lifecycle review." } };
      }
      if (preview.proposal.retained_disposition === "reset") {
        return {
          ok: false,
          error: {
            code: "PLC_CONTROLLER_RETAINED_RESET_REQUIRES_MANUAL_REVIEW",
            message: "This reviewed action resets retained values and requires the manual Gateway review and acknowledgement flow.",
          },
        };
      }
      if (preview.proposal.force_disposition === "clear") {
        return {
          ok: false,
          error: {
            code: "PLC_CONTROLLER_FORCE_CLEAR_REQUIRES_MANUAL_REVIEW",
            message: "This reviewed action clears active forces and requires the manual Gateway review and acknowledgement flow.",
          },
        };
      }
      const opened = dispatch("gw-ai-propose-plc-controller-lifecycle", {
        payload: {
          controller_name: controller.name,
          target_name: controller.target?.name || "",
          action,
          reason,
          preview,
        },
      });
      if (!opened.ok) return opened;
      const proposal = preview.proposal;
      return {
        ok: true,
        data: {
          controller_name: controller.name,
          target_name: controller.target?.name || "",
          requested_action: proposal.requested_action,
          command_action: proposal.command_action,
          desired_state: proposal.desired_state,
          revision: proposal.revision,
          generation: proposal.generation,
          retained_disposition: proposal.retained_disposition,
          force_disposition: proposal.force_disposition,
          applications: proposal.applications,
          policy: proposal.policy || null,
          warnings: proposal.warnings,
          proposal_sha256: proposal.proposal_sha256,
          expires_at: proposal.expires_at,
          confirmation_required: true,
          opened_for_human_review: true,
        },
      };
    } catch (error) {
      return {
        ok: false,
        error: {
          code: "PLC_CONTROLLER_LIFECYCLE_PREVIEW_FAILED",
          message: error?.message || "Controller lifecycle review is unavailable on this runtime.",
        },
      };
    }
  },
};

export const propose_opcua_server_role_change = {
  name: "propose_opcua_server_role_change",
  description:
    "Open Servers and ask the mounted human page to create a backend-reviewed proposal for public non-secret endpoint, namespace, identity-mode, source-network, or resource-limit fields of the disabled OPC UA server desired revision. Namespace mappings and all certificate/key/trust references remain human-only. Call get_opcua_server_role_config and get_opcua_server_role_status first. This tool itself never reads configure-only references, calls Preview, or possesses a proposal token/digest; the page performs the fresh review. It cannot confirm, apply, enable, restart, or open a listener.",
  parameters: {
    type: "object",
    properties: {
      reason: { type: "string", minLength: 3, maxLength: 500 },
      listen_address: { type: "string", minLength: 3, maxLength: 128 },
      namespace_uri: { type: "string", minLength: 5, maxLength: 512 },
      source_cidrs: { type: "array", maxItems: 64, items: { type: "string", minLength: 2, maxLength: 128 } },
      identity_mode: { type: "string", enum: ["username_or_certificate", "certificate_only"] },
      identity_provider: { type: "string", minLength: 1, maxLength: 512 },
      max_channels: { type: "integer", minimum: 1, maximum: 256 },
      max_sessions: { type: "integer", minimum: 1, maximum: 256 },
      max_subscriptions_per_session: { type: "integer", minimum: 1, maximum: 1024 },
      max_monitored_items_per_subscription: { type: "integer", minimum: 1, maximum: 65536 },
      max_nodes_per_read: { type: "integer", minimum: 1, maximum: 4096 },
      max_nodes_per_write: { type: "integer", minimum: 1, maximum: 1024 },
    },
    required: ["reason"],
    additionalProperties: false,
  },
  execute: async ({ reason, ...changes }) => {
    const normalizedReason = String(reason || "").trim();
    const publicChanges = Object.fromEntries(
      Object.entries(changes).filter(([field]) => OPCUA_AI_PUBLIC_FIELDS.has(field))
    );
    if (Object.keys(publicChanges).length === 0) {
      return { ok: false, error: { code: "OPCUA_SERVER_CHANGE_REQUIRED", message: "Provide at least one public OPC UA desired-configuration change." } };
    }
    const opened = dispatch("gw-ai-propose-opcua-server-role", {
      payload: { changes: publicChanges, reason: normalizedReason },
    });
    if (!opened.ok) return opened;
    return {
      ok: true,
      data: {
        desired_state: "disabled",
        requested_public_fields: Object.keys(publicChanges).sort(),
        confirmation_required: true,
        opened_for_human_review: true,
        review_pending_in_page: true,
        applied: false,
        listening: false,
      },
    };
  },
};

const proposeTools = [
  propose_datasource,
  propose_api_profile,
  propose_credential,
  propose_oauth_app,
  propose_mqtt_broker,
  propose_historian_provider,
  propose_user,
  propose_group,
  propose_project,
  propose_ai_provider,
  propose_alarm,
  propose_edge_gpio_binding,
  propose_edge_modbus_rtu_connection,
  propose_edge_modbus_tcp_server,
  propose_plc_fleet_rollout,
  propose_plc_fleet_control,
  propose_plc_controller_draft_change,
  propose_plc_controller_lifecycle,
  propose_opcua_server_role_change,
];

export default proposeTools;
